import { Heart, Moon, Droplets, Dumbbell, Stethoscope, ChevronRight } from "lucide-react";

/**
 * Realistic smartphone mockup built with pure HTML/Tailwind.
 * Displays the Health Risk Score, circular progress ring, and lifestyle recommendations.
 */
export default function PhoneMockup({ compact = false }) {
  const score = 82;
  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference * (1 - score / 100);

  return (
    <div
      data-testid="phone-mockup"
      className={`relative ${compact ? "w-[280px]" : "w-[320px]"} mx-auto`}
    >
      {/* Device frame */}
      <div className="relative rounded-[3rem] bg-gradient-to-b from-zinc-800 to-zinc-950 p-[10px] shadow-[0_50px_100px_-20px_rgba(0,229,255,0.35),0_30px_60px_-30px_rgba(11,61,145,0.6)] border border-white/10">
        {/* Side buttons */}
        <div className="absolute -left-[3px] top-24 w-[3px] h-8 rounded-l bg-zinc-700" />
        <div className="absolute -left-[3px] top-36 w-[3px] h-14 rounded-l bg-zinc-700" />
        <div className="absolute -right-[3px] top-32 w-[3px] h-20 rounded-r bg-zinc-700" />

        <div className="relative rounded-[2.4rem] bg-[#05050A] overflow-hidden aspect-[9/19.5]">
          {/* Notch/Dynamic island */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-20 border border-white/5" />

          {/* Screen content */}
          <div className="relative pt-12 px-5 pb-6 h-full flex flex-col">
            {/* Status bar */}
            <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-4">
              <span>9:41</span>
              <div className="flex items-center gap-1">
                <span>􀟪</span>
                <span>􀛨</span>
              </div>
            </div>

            {/* Header */}
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-cyan-400">
                Health Risk Score
              </p>
              <h3 className="text-white font-display font-semibold text-lg mt-1">
                Overall Wellness
              </h3>
            </div>

            {/* Circular Score */}
            <div className="mt-4 flex justify-center">
              <div className="relative">
                <svg width="140" height="140" viewBox="0 0 120 120" className="-rotate-90">
                  <circle
                    cx="60"
                    cy="60"
                    r="54"
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r="54"
                    stroke="url(#scoreGrad)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    style={{ transition: "stroke-dashoffset 1.2s ease" }}
                  />
                  <defs>
                    <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#00E5FF" />
                      <stop offset="100%" stopColor="#0B3D91" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-display text-4xl font-semibold text-white">
                    {score}
                    <span className="text-lg text-zinc-500">%</span>
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-cyan-400 mt-0.5">
                    Excellent
                  </span>
                </div>
              </div>
            </div>

            {/* Recommendations */}
            <div className="mt-5">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-2.5">
                Lifestyle Recommendations
              </p>
              <div className="space-y-2">
                {[
                  { icon: Moon, label: "Improve Sleep" },
                  { icon: Droplets, label: "Stay Hydrated" },
                  { icon: Dumbbell, label: "Exercise Regularly" },
                  { icon: Stethoscope, label: "Routine check-up" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/5"
                  >
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shrink-0">
                      <item.icon className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-xs text-zinc-200 flex-1">{item.label}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom heart pulse */}
            <div className="mt-auto flex items-center justify-center gap-2 pt-4">
              <Heart className="w-3.5 h-3.5 text-cyan-400 fill-cyan-400/50" />
              <span className="text-[10px] text-zinc-500">Analysis complete</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
